import React, { useState, useEffect, useCallback } from 'react';
import {
  Table, Tag, Button, Input, Select, DatePicker, Form,
  Row, Col, Tooltip, Dropdown, Modal, Space, Segmented, Collapse, Alert, Switch,
} from 'antd';
import {
  PlusOutlined, ReloadOutlined,
  MoreOutlined, EyeOutlined, SwapOutlined, UserAddOutlined, CheckCircleOutlined,
  UserSwitchOutlined, UsergroupAddOutlined,
  FolderOpenOutlined, MobileOutlined, SearchOutlined, LinkOutlined,
} from '@ant-design/icons';
import TagRc from '../../components/TagRc';
import BadgeCanal from './components/mobile/BadgeCanal';
import { PastilleNonAffecte } from './components/NonAffecte';
import TableMembresLiaison from './components/TableMembresLiaison';
import { liaisonService } from '../../services/liaisonService';
import KpiCards from '../../components/KpiCards';
import { ICONE_PHASE } from './iconesPhases';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import TableExportButton from '../../components/TableExportButton';
import ChangerStatutModal from './components/ChangerStatutModal';
import AffecterDossierModal from './components/AffecterDossierModal';
import CBModal from '../../components/CBModal';
import AffecterEnMasseModal from './components/AffecterEnMasseModal';
import axiosInstance from '../../variables/api/axiosInstance';
import {
  formatDate, STATUTS_SINISTRE, STATUT_MAP, getUser, can, HAB,
  PHASES_SINISTRE, compterPhase, compterTousStatuts,
} from '../../variables/constants';
import { affectationService } from '../../services/affectationService';
import mobileService from '../../services/mobileService';
import { toast } from 'react-toastify';
import PageHeader from '../../components/PageHeader';
import { filtreSansAccents } from '../../utils/rechercheTexte';
import { Trans, useTranslation } from 'react-i18next';
import { avecLibelleTraduit } from '../../i18n/libelles';

const { Search } = Input;
const { RangePicker } = DatePicker;
const { Option } = Select;

const GREEN = '#1a6b3c';

/**
 * Criteres de la recherche avancee.
 *
 * Le champ libre existant applique UN terme en OU sur onze colonnes : parfait
 * pour « je crois me souvenir de quelque chose », impuissant pour « cette
 * immatriculation ET cette police ». Et il ignore deux choses que les agents
 * cherchent tous les jours : le numero e-Carte Brune, et les personnes
 * impliquees - l'entite Victime porte aussi les parties adverses.
 */
const CRITERES_AVANCES = [
  {
    cle: 'immatriculation', libelle: 'liste.criteres.immatriculation', exemple: 'TG 4521 AB',
    aide: 'liste.criteres.immatriculationAide'
  },
  {
    cle: 'personne', libelle: 'liste.criteres.personne', exemple: 'Mensah',
    aide: 'liste.criteres.personneAide'
  },
  { cle: 'numeroPolice', libelle: 'liste.criteres.numeroPolice', exemple: '2026-004512' },
  { cle: 'numeroEcarteBrune', libelle: 'liste.criteres.numeroEcarteBrune', exemple: 'CB/TG/0091' },
  { cle: 'numeroHomologue', libelle: 'liste.criteres.numeroHomologue', exemple: 'NG/2026/0088' },
  { cle: 'numeroChassis', libelle: 'liste.criteres.numeroChassis', exemple: 'JT2BF22K1W0123456' },
  {
    cle: 'numeroLiaison', libelle: 'liste.criteres.numeroLiaison', exemple: 'SL-2026-0004',
    aide: 'liste.criteres.numeroLiaisonAide'
  },
];

const LIBELLE_CRITERE = Object.fromEntries(
  CRITERES_AVANCES.map((c) => [c.cle, c.libelle]),
);

/* Les trois origines, pour le sous-titre de l'export. Cle de traduction, lue au
   moment du rendu : ce module est charge une fois, la langue peut changer apres. */
const LIBELLE_ORIGINE = {
  REPRIS: 'liste.filtres.repris',
  GUICHET: 'liste.filtres.saisisGuichet',
  MOBILE: 'liste.filtres.declaresMobile',
};
const TEXT = '#111827';
const MUTED = '#6b7280';
const LIGHT = '#f9fafb';

const TYPE_META = avecLibelleTraduit({
  SURVENU_TOGO: { label: 'ET', color: 'geekblue' },
  SURVENU_ETRANGER: { label: 'TE', color: 'purple' },
}, 'sinistres:liste.typeSinistre', { champ: 'text' });

const DOMMAGE_META = avecLibelleTraduit({
  CORPOREL: { color: 'red' },
  MATERIEL: { color: 'orange' },
  MIXTE: { color: 'volcano' },
}, 'sinistres:liste.typeDommage');

// Correspondance couleur Tag antd -> hex, utilisée uniquement pour dériver
// les fonds pastel des cards KPI (KpiCards attend une couleur hex).
const ANTD_COLOR_HEX = {
  default: '#6b7280', blue: '#2563eb', green: '#16a34a', red: '#dc2626',
  orange: '#f97316', gold: '#ca8a04', purple: '#7c3aed', volcano: '#ea580c',
  geekblue: '#4338ca', cyan: '#0891b2', magenta: '#db2777', lime: '#65a30d',
};

/**
 * Vues nommees - le travail, pas le modele.
 *
 * Un redacteur ne pense pas « statut = ATTENTE_PIECES_DE_RECLAMATION », il pense
 * « les dossiers dont j'attends des pieces ». Ces raccourcis portent le nom du
 * travail ; les filtres fins restent disponibles, replies, pour les cas particuliers.
 *
 * CONTRAINTE : le backend ne filtre que sur UN statut a la fois. Chaque vue se
 * ramene donc a un seul critere - pas de vue « En reglement » qui couvrirait huit
 * statuts, elle ne serait pas servable et afficherait silencieusement autre chose
 * que son intitule.
 */
const VUES = avecLibelleTraduit([
  { cle: 'tous', criteres: {} },
  { cle: 'miens', propreAgent: true },
  { cle: 'a-valider', criteres: { statut: 'NOUVEAU' } },
  { cle: 'pieces', criteres: { statut: 'ATTENTE_PIECES_DE_RECLAMATION' } },
  { cle: 'murs', criteres: { statut: 'MUR' } },
  { cle: 'bap', criteres: { statut: 'BAP' } },
  { cle: 'litige', criteres: { litige: 'EN_LITIGE' } },
], 'sinistres:liste.vues', { cle: 'cle', champ: 'libelle' });

const Dash = () => <span style={{ color: '#9ca3af' }}>-</span>;

const MetaLine = ({ children, strong = false }) => (
  <div
    style={{
      minWidth: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      color: strong ? TEXT : MUTED,
      fontSize: strong ? 13 : 12,
      fontWeight: strong ? 650 : 400,
      lineHeight: strong ? '20px' : '18px',
    }}
    title={typeof children === 'string' ? children : undefined}
  >
    {children || <Dash />}
  </div>
);

/*  Badge statut  */
const StatutBadge = ({ statut }) => {
  const s = STATUT_MAP[statut] || { label: statut, color: 'default' };
  return (
    <Tag color={s.color} style={{ borderRadius: 18, fontSize: 11, fontWeight: 650, marginInlineEnd: 0 }}>
      {s.label}
    </Tag>
  );
};

export default function ListSinistres() {
  const { t } = useTranslation('sinistres');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [sinistres, setSinistres] = useState([]);
  /* Membres des liaisons presentes sur la page courante, indexes par
     liaisonTrackingId. Charges en UN appel apres la liste : derouler une ligne ne
     declenche aucune requete. */
  const [liaisonsParId, setLiaisonsParId] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filtreStatut, setFiltreStatut] = useState('');
  const [dateRange, setDateRange] = useState(null);
  const [filtreLitige, setFiltreLitige] = useState(''); // '' | EN_LITIGE | HORS_LITIGE | CONTENTIEUX | ARBITRAGE
  // ET (SURVENU_TOGO) / TE (SURVENU_ETRANGER). La distinction pilote déjà
  // l'arborescence GED, les 5 % de frais de gestion et la bifurcation du flux
  // garantie ; elle était affichée dans la colonne Dossier mais on ne pouvait
  // pas s'en servir pour filtrer.
  const [filtreType, setFiltreType] = useState('');
  /* Recherche avancée : critères nommés combinés en ET, là où le champ libre
     applique un terme unique en OU sur onze colonnes. C'est ce qui permet de
     retrouver un dossier par la plaque d'une partie adverse ou par le numéro
     e-Carte Brune - deux choses que le champ libre ne voyait pas. */
  // « En attente d'un tiers » : ce qui sépare « pas traité » de « traité, mais
  // bloqué par quelqu'un d'extérieur au bureau ».
  const [filtreAttente, setFiltreAttente] = useState('');
  const [avanceeOuverte, setAvanceeOuverte] = useState(false);
  const [criteres, setCriteres] = useState(null);
  /* Origine du dossier : '' (tous) | REPRIS | GUICHET | MOBILE.
     TROIS ENSEMBLES DISJOINTS, et non deux. Le filtre ne connaissait que
     `repriseHistorique`, un booleen - repris, ou « saisi ». Le canal mobile est
     arrive apres, dans une AUTRE colonne (`canal`) : un dossier mobile n'etant
     pas repris, il tombait dans « saisis » avec ceux du guichet, sans aucun
     moyen de l'en extraire. Les deux colonnes repondent a deux questions - d'ou
     vient la donnee, et par quel canal elle est entree - et il faut les deux
     pour nommer l'origine. */
  const [filtreOrigine, setFiltreOrigine] = useState('');
  const [filtreRedacteur, setFiltreRedacteur] = useState(''); // trackingId de l'agent, ou '' pour tous
  const [agents, setAgents] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 25, total: 0 });
  const [statutModal, setStatutModal] = useState({ open: false, sinistre: null });
  const [affectModal, setAffectModal] = useState({ open: false, sinistre: null });
  /* Sélection multiple : hors mode sélection, un clic sur une ligne ouvre le
     dossier. Les deux gestes ne peuvent pas coexister sur la même ligne, d'où
     l'interrupteur explicite plutôt que des cases toujours affichées. */
  const [modeSelection, setModeSelection] = useState(false);
  const [selection, setSelection] = useState([]);
  const [affectLotOpen, setAffectLotOpen] = useState(false);
  const [density, setDensity] = useState('comfortable');
  const { current, pageSize, total } = pagination;

  // KPI par statut - indépendant de la pagination affichée
  const [kpiCounts, setKpiCounts] = useState({});
  // Vrai des le premier rendu : sinon la frame initiale affiche 0 sur les 28 cartes
  // -- le chargement ressemblerait au zero legitime que afficherCompteur bannit.
  const [kpiLoading, setKpiLoading] = useState(true);
  const [kpiErreur, setKpiErreur] = useState(false);
  // Nouveaux du jour + coherence garantie/validation (Task 6) : chargés en même temps
  // que kpiCounts mais avec un echec independant -- null = pas encore charge ou echec,
  // jamais confondu avec un zero legitime.
  const [coherence, setCoherence] = useState(null);
  // Déclarations MOBILES à valider. Ce n'est pas `kpiCounts.SOUMISE` : depuis la
  // corbeille, les déclarations du guichet en attente d'affectation sont SOUMISE
  // aussi, et le bouton les annonçait comme des « déclarations mobiles ».
  const [nbMobilesAValider, setNbMobilesAValider] = useState(0);
  useEffect(() => {
    if (!can(HAB.MOBILE_DECLARATION_READ)) return undefined;
    let monte = true;
    mobileService.declarations({ page: 1, size: 1 })
      .then((r) => { if (monte) setNbMobilesAValider(r.total || 0); })
      .catch(() => { if (monte) setNbMobilesAValider(0); });
    return () => { monte = false; };
  }, []);

  useEffect(() => {
    const ids = [...new Set(sinistres.map(s => s.liaisonTrackingId).filter(Boolean))];
    if (ids.length === 0) { setLiaisonsParId({}); return; }

    let ignore = false;
    liaisonService.lot(ids)
      .then((r) => {
        if (ignore) return;
        const parId = {};
        (r.data?.data || []).forEach((l) => { parId[l.liaisonTrackingId] = l; });
        setLiaisonsParId(parId);
      })
      .catch(() => { if (!ignore) setLiaisonsParId({}); });
    return () => { ignore = true; };
  }, [sinistres]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(timer);
  }, [search]);

  const goFirstPage = useCallback(() => setPagination(p => ({ ...p, current: 1 })), []);

  const loadSinistres = useCallback(async (page = 1, pageSize = 25) => {
    setLoading(true);
    try {
      const params = {
        page: Math.max(page - 1, 0),
        size: pageSize,
      };
      if (debouncedSearch) params.search = debouncedSearch;
      if (filtreStatut) params.statut = filtreStatut;
      if (filtreLitige) params.litige = filtreLitige;
      /* Origine : la reprise historique se lit sur `repriseHistorique`, le canal de
         saisie sur `canal`. « Guichet » exige les deux - un dossier repris porte
         lui aussi canal = WEB, puisque c'est la valeur par defaut de la colonne. */
      if (filtreOrigine === 'REPRIS') params.repriseHistorique = true;
      if (filtreOrigine === 'GUICHET') { params.repriseHistorique = false; params.canal = 'WEB'; }
      if (filtreOrigine === 'MOBILE') { params.repriseHistorique = false; params.canal = 'MOBILE'; }
      if (dateRange?.[0]) params.dateDebut = dateRange[0].format('YYYY-MM-DD');
      if (dateRange?.[1]) params.dateFin = dateRange[1].format('YYYY-MM-DD');
      // « Mes dossiers » n'est qu'un filtre rédacteur sur soi-même : le backend n'a
      // pas de cas particulier, le front connaît son propre trackingId.
      if (filtreRedacteur) params.redacteur = filtreRedacteur;
      if (filtreType) params.typeSinistre = filtreType;
      if (filtreAttente) params.enAttenteTiers = filtreAttente === 'OUI';

      // En POST quand des critères sont posés : ils contiennent des noms de
      // personnes, qu'une query string inscrirait dans les journaux du serveur
      // web et dans l'historique du navigateur.
      const res = criteres
        ? await axiosInstance.post('/v1/sinistres/recherche-avancee', criteres,
          { params: { page: params.page, size: params.size } })
        : await axiosInstance.get('/v1/sinistres', { params });
      const body = res.data || {};
      setSinistres(Array.isArray(body.data) ? body.data : []);
      setPagination({
        current: (body.currentPage ?? 0) + 1,
        pageSize: body.pageSize || pageSize,
        total: body.totalElements || 0,
      });
    } catch (err) {
      toast.error(err?.response?.data?.message || t('liste.messages.erreurChargement'));
      setSinistres([]);
      setPagination(p => ({ ...p, total: 0 }));
    } finally { setLoading(false); }
  }, [debouncedSearch, filtreStatut, filtreLitige, filtreOrigine, dateRange, filtreRedacteur, filtreType, filtreAttente, criteres, t]);

  useEffect(() => { loadSinistres(current, pageSize); }, [loadSinistres, current, pageSize]);

  /* Liaisons de la page : un seul appel pour toutes, jamais un par ligne.
     L'echec est silencieux - le deroule est un confort, pas la raison d'etre de
     cette page, et un toast de plus sur un chargement secondaire serait du bruit. */
  // useEffect(() => {
  //   const ids = [...new Set(sinistres.map(s => s.liaisonTrackingId).filter(Boolean))];
  //   if (ids.length === 0) { setMembresParLiaison({}); return; }

  //   let ignore = false;
  //   liaisonService.lot(ids)
  //     .then((r) => {
  //       if (ignore) return;
  //       const parId = {};
  //       (r.data?.data || []).forEach((l) => { parId[l.liaisonTrackingId] = l.membres || []; });
  //       setMembresParLiaison(parId);
  //     })
  //     .catch(() => { if (!ignore) setMembresParLiaison({}); });
  //   return () => { ignore = true; };
  // }, [sinistres]);

  const tableData = React.useMemo(() => {
    const dejaGroupe = new Set();
    const rows = [];
    for (const r of sinistres) {
      if (r.liaisonTrackingId && liaisonsParId[r.liaisonTrackingId]) {
        if (dejaGroupe.has(r.liaisonTrackingId)) continue; // le groupe existe déjà
        dejaGroupe.add(r.liaisonTrackingId);
        rows.push({
          estGroupeLiaison: true,
          key: `liaison-${r.liaisonTrackingId}`,
          liaison: liaisonsParId[r.liaisonTrackingId],
        });
      } else {
        rows.push(r);
      }
    }
    return rows;
  }, [sinistres, liaisonsParId]);

  useEffect(() => { goFirstPage(); }, [debouncedSearch, goFirstPage]);

  // Charge le nombre de dossiers par statut pour les cards KPI. Volontairement
  // indépendant des filtres de la table (recherche, RC, dates…) pour garder
  // une vue d'ensemble stable ; seul un clic sur une card applique le filtre.
  const loadKpiStats = useCallback(async () => {
    setKpiLoading(true);
    // Les deux appels partent en parallele et sont juges independamment : l'echec de
    // coherence-garantie ne doit pas faire disparaitre les comptes par statut.
    //
    // `/v1/stats/nouveaux-du-jour` a ete RETIRE : sa seule valeur alimentait le
    // sous-titre « X declares aujourd'hui », supprime. Le garder aurait fait un
    // aller-retour reseau a chaque ouverture de la liste pour un nombre que plus
    // personne n'affiche.
    const [comptesRes, coherenceRes] = await Promise.allSettled([
      // Un seul appel : le backend renvoie la repartition complete. Les statuts sans
      // dossier sont absents de la reponse, et c'est voulu -- les lecteurs de kpiCounts
      // font deja `?? 0`.
      axiosInstance.get('/v1/sinistres/comptes-par-statut'),
      axiosInstance.get('/v1/stats/coherence-garantie'),
    ]);

    if (comptesRes.status === 'fulfilled') {
      setKpiCounts(comptesRes.value.data?.data || {});
      setKpiErreur(false);
    } else {
      setKpiCounts({});
      setKpiErreur(true);
      toast.error(t('liste.messages.indicateursIndisponibles'));
    }

    setCoherence(coherenceRes.status === 'fulfilled' ? (coherenceRes.value.data?.data ?? null) : null);

    setKpiLoading(false);
  }, [t]);

  useEffect(() => { loadKpiStats(); }, [loadKpiStats]);

  // NOUVEAU→VALIDE a ete retire de la machine d'etats : cette action rapide
  // declenche desormais la demande de confirmation de garantie (meme sous-processus
  // que le bouton equivalent de DetailsSinistre.js), plus la phase de confirmation.
  const demanderConfirmationGarantie = useCallback((sinistre) => {
    Modal.confirm({
      title: t('liste.demandeGarantie.titre'),
      icon: <CheckCircleOutlined style={{ color: GREEN }} />,
      content: (
        <div>
          <p><Trans t={t} i18nKey="liste.demandeGarantie.question" values={{ numero: sinistre.numeroSinistreLocal }} components={{ strong: <strong /> }} /></p>
          <p style={{ color: '#6b7280', fontSize: 12, marginTop: 4 }}>
            {t('liste.demandeGarantie.explication')}
          </p>
        </div>
      ),
      okText: t('liste.demandeGarantie.confirmer'),
      cancelText: t('liste.demandeGarantie.annuler'),
      okButtonProps: { style: { background: GREEN, borderColor: GREEN } },
      onOk: async () => {
        try {
          const res = await axiosInstance.post(`/v1/sinistres/${sinistre.sinistreTrackingId}/demander-confirmation-garantie`);
          toast.success(res.data?.message || t('liste.demandeGarantie.envoyee'));
          loadSinistres(current, pageSize);
          loadKpiStats();
        } catch (err) {
          toast.error(err?.response?.data?.message || t('liste.demandeGarantie.erreur'));
        }
      },
    });
  }, [loadSinistres, loadKpiStats, current, pageSize, t]);

  // Applique les filtres passés par query string (ex: depuis le dashboard)
  useEffect(() => {
    const st = searchParams.get('statut');
    if (st) { setFiltreStatut(st); goFirstPage(); }
    const litige = searchParams.get('litige');
    if (litige) { setFiltreLitige(litige); goFirstPage(); }
    // Numero de dossier cite dans une conversation : quand il ne se resout pas a
    // un dossier unique, on arrive ici avec la recherche deja posee, plutot que
    // sur une liste entiere ou l'utilisateur devrait retaper le numero.
    const q = searchParams.get('search');
    if (q) { setSearch(q); goFirstPage(); }
  }, [searchParams, goFirstPage]);

  const resetFilters = () => {
    setSearch('');
    setFiltreStatut('');
    setFiltreLitige('');
    setFiltreOrigine('');
    setFiltreRedacteur('');
    setFiltreType('');
    setFiltreAttente('');
    setDateRange(null);
    setCriteres(null);
    setAvanceeOuverte(false);
    goFirstPage();
  };

  const activeFiltersCount = [
    debouncedSearch, filtreStatut, filtreLitige, filtreOrigine, filtreRedacteur, filtreType,
    filtreAttente, criteres,
    dateRange?.[0] && dateRange?.[1],
  ].filter(Boolean).length;

  // Liste des agents pour le filtre « traité par ». Réservée à ceux qui affectent :
  // c'est la même habilitation qui garde l'endpoint côté backend.
  const peutVoirLesAgents = can(HAB.SINISTRE_ASSIGN);
  useEffect(() => {
    if (!peutVoirLesAgents) return;
    affectationService.utilisateursAffectables()
      .then((r) => setAgents(r.data?.data ?? []))
      .catch(() => setAgents([]));
  }, [peutVoirLesAgents]);

  const moi = getUser();

  /* La vue active se DEDUIT de l'etat des filtres, elle n'est pas memorisee a part :
     un utilisateur qui bricole les filtres fins verrait sinon une puce rester
     allumee sur une vue qui ne correspond plus a ce qu'il regarde. */
  const vueActive = (() => {
    const aucunAutre = !debouncedSearch && !filtreOrigine && !dateRange?.[0];
    if (!aucunAutre) return null;
    if (filtreRedacteur && !filtreStatut && !filtreLitige) return 'miens';
    if (filtreRedacteur) return null;
    if (filtreLitige === 'EN_LITIGE' && !filtreStatut) return 'litige';
    if (filtreLitige) return null;
    if (!filtreStatut) return 'tous';
    return VUES.find((v) => v.criteres?.statut === filtreStatut)?.cle ?? null;
  })();

  /* Une vue REMPLACE le contexte, elle ne s'ajoute pas a lui : cumuler « Mes dossiers »
     avec un statut laisse par un clic precedent donnerait une liste vide qu'on
     mettrait sur le compte d'un bug. */
  const appliquerVue = (vue) => {
    setSearch('');
    setFiltreStatut('');
    setFiltreLitige('');
    setFiltreOrigine('');
    setDateRange(null);
    setFiltreRedacteur(vue.propreAgent ? (moi?.trackingId || '') : '');
    if (vue.criteres?.statut) setFiltreStatut(vue.criteres.statut);
    if (vue.criteres?.litige) setFiltreLitige(vue.criteres.litige);
    goFirstPage();
  };

  const tableSize = density === 'compact' ? 'small' : 'middle';
  const rowPadding = density === 'compact' ? 2 : 6;

  // Filtre la card KPI cliquée sur le statut, ou la désactive si déjà active.
  const basculerFiltreStatut = (statutValue) => {
    setFiltreStatut(filtreStatut === statutValue ? '' : statutValue);
    goFirstPage();
  };

  // Trois etats distincts : chargement, echec, valeur. Un echec ne doit pas
  // ressembler a un zero legitime -- c'est tout l'objet de ce helper.
  const afficherCompteur = (n) => (kpiLoading ? '…' : kpiErreur ? '-' : n);


  const kpiItems = [
    {
      label: t('liste.kpis.tousStatuts'),
      value: afficherCompteur(compterTousStatuts(kpiCounts)),
      color: GREEN,
      active: !filtreStatut,
      onClick: () => { setFiltreStatut(''); goFirstPage(); },
    },
    ...STATUTS_SINISTRE.map((s) => ({
      label: s.label,
      value: afficherCompteur(kpiCounts[s.value] ?? 0),
      color: ANTD_COLOR_HEX[s.color] || GREEN,
      active: filtreStatut === s.value,
      onClick: () => basculerFiltreStatut(s.value),
    })),
  ];

  // Indicateurs cles, toujours visibles au-dessus du tiroir. Informatifs : le
  // backend ne filtre que sur UN statut, une carte agregee ne peut donc pas
  // filtrer le tableau au clic. D'ou l'absence de onClick et de active.
  // Quatre cartes, et quatre seulement : le total et les trois phases du cycle de vie
  // qui s'y additionnent. On lit d'un coup d'oeil ou est le portefeuille.
  //
  // Deux indicateurs ont ete SORTIS de cette rangee, non pas supprimes :
  //   - « Declarations mobiles » doublonnait le bouton d'en-tete, qui lui est
  //     actionnable et n'apparait que s'il y a quelque chose a traiter ;
  //   - « Nouveaux aujourd'hui » est passe en sous-titre de page, ou il se lit
  //     comme la phrase qu'il est plutot que comme un cinquieme chiffre a comparer.
  // La phase DECLARATION reste comptee dans le total et dans le tiroir par statut.
  const kpiCles = [
    {
      label: t('liste.kpis.totalDossiers'),
      value: afficherCompteur(compterTousStatuts(kpiCounts)),
      color: GREEN,
      icon: <FolderOpenOutlined />,
    },
    ...PHASES_SINISTRE.filter((p) => p.key !== 'DECLARATION').map((p) => ({
      label: p.label,
      value: afficherCompteur(compterPhase(kpiCounts, p)),
      color: p.color,
      icon: ICONE_PHASE[p.key],
    })),
  ];

  const renderRc = (r) => {
    if (!r.positionRc || r.positionRc === 'EN_ATTENTE') {
      const enPhaseRc = ['ATTENTE_RC', 'ATTENTE_PIECES_DE_RECLAMATION', 'MUR'].includes(r.statut);
      if (!enPhaseRc) return <Dash />;
    }
    return (
      <TagRc
        positionRc={r.positionRc}
        pourcentageRcFinal={r.tauxRc != null ? Number(r.tauxRc) : null}
        size="sm"
      />
    );
  };

  const columns = [
    {
      title: t('liste.colonnes.dossier'),
      key: 'dossier',
      width: 310,
      minHeight: 110,
      fixed: 'left',
      onCell: (r) => r.estGroupeLiaison ? { colSpan: columns.length } : {},
      render: (_, r) => {
        if (r.estGroupeLiaison) {
          const l = r.liaison;
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <LinkOutlined style={{ color: '#4338ca' }} />
              <strong>{l.numeroLiaison}</strong>
              <Tag color="geekblue">{t('liste.liaison.dossiersLies', { n: l.nombreDossiers })}</Tag>
              <span style={{ color: MUTED, fontSize: 12 }}>
                {TYPE_META[l.typeSinistre]?.text} {t('liste.liaison.derniereAction', { date: formatDate(l.derniereAction) })}
              </span>
            </div>
          );
        }

        const v = r.numeroSinistreLocal;
        return (
          <div style={{ padding: `${rowPadding}px 0`, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, marginBottom: 4 }}>
              <button
                onClick={(e) => { e.stopPropagation(); navigate(`/sinistres/${r.sinistreTrackingId}`); }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: GREEN,
                  fontWeight: 800,
                  cursor: 'pointer',
                  padding: 0,
                  fontSize: 13,
                  fontVariantNumeric: 'tabular-nums',
                  maxWidth: 168,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={v}
              >
                {v}
              </button>
              <Tag
                color={TYPE_META[r.typeSinistre]?.color || 'default'}
                style={{ borderRadius: 14, fontSize: 10, fontWeight: 800, marginInlineEnd: 0 }}
              >
                {TYPE_META[r.typeSinistre]?.label || r.typeSinistre || '-'}
              </Tag>
              <Tag
                color={DOMMAGE_META[r.typeDommage]?.color || 'default'}
                style={{ borderRadius: 14, fontSize: 10, marginInlineEnd: 0 }}
              >
                {DOMMAGE_META[r.typeDommage]?.label || r.typeDommage || '-'}
              </Tag>
              <BadgeCanal canal={r.canal} size="sm" />
              <PastilleNonAffecte sinistre={r} size="sm" />
              {r.repriseHistorique && (
                <Tooltip title={t('liste.colonnes.donneeReprise')}>
                  <Tag color="purple" style={{ borderRadius: 14, fontSize: 10, fontWeight: 800, marginInlineEnd: 0 }}>
                    R
                  </Tag>
                </Tooltip>
              )}
            </div>
            <MetaLine>{r.numeroSinistreHomologue || r.numeroSinistreAssureur || t('liste.colonnes.referenceHomologueNonRenseignee')}</MetaLine>
            <MetaLine>{TYPE_META[r.typeSinistre]?.text}</MetaLine>
          </div>
        );
      },
    },
    {
      title: t('liste.colonnes.assureVehicule'),
      dataIndex: 'assureNomComplet',
      key: 'assure',
      width: 240,
      ellipsis: true,
      onCell: (r) => r.estGroupeLiaison ? { colSpan: 0 } : {},
      render: (v, r) => (
        <div style={{ minWidth: 0 }}>
          <MetaLine strong>{v}</MetaLine>
          <MetaLine>{r.assureImmatriculation ? t('liste.colonnes.immat', { immatriculation: r.assureImmatriculation }) : t('liste.colonnes.immatriculationNonRenseignee')}</MetaLine>
          <MetaLine>{(r.assureNumeroPolice || r.numeroPoliceAssureur) ? t('liste.colonnes.police', { numero: r.assureNumeroPolice || r.numeroPoliceAssureur }) : null}</MetaLine>
        </div>
      ),
    },
    {
      title: t('liste.colonnes.homologue'),
      dataIndex: 'paysEmetteurLibelle',
      key: 'partenaire',
      width: 240,
      ellipsis: true,
      onCell: (r) => r.estGroupeLiaison ? { colSpan: 0 } : {},
      render: (v, r) => (
        <div style={{ minWidth: 0 }}>
          <MetaLine strong>{v || r.paysGestionnaireLibelle}</MetaLine>
          <MetaLine>{r.organismeMembreRaisonSociale || r.organismeHomologueRaisonSociale}</MetaLine>
          <MetaLine>{r.organismeMembreCode ? t('liste.colonnes.code', { code: r.organismeMembreCode }) : null}</MetaLine>
        </div>
      ),
    },
    {
      title: t('liste.colonnes.avancement'),
      dataIndex: 'statut',
      key: 'avancement',
      width: 230,
      onCell: (r) => r.estGroupeLiaison ? { colSpan: 0 } : {},
      export: (v, r) => {
        const statut = STATUT_MAP[v]?.label || v || '';
        const rc = (!r?.positionRc || r.positionRc === 'EN_ATTENTE')
          ? ''
          : r.positionRc === 'TRANCHEE' && r?.tauxRc != null
            ? t('liste.export.rcTranchee', { taux: r.tauxRc })
            : t('liste.export.rc', { position: r.positionRc });
        return [statut, rc].filter(Boolean).join(' - ');
      },
      render: (v, r) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-start' }}>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <StatutBadge statut={v} />
            {r.estContentieux && (
              <Tag color="red" style={{ borderRadius: 18, fontSize: 11, marginInlineEnd: 0 }}>{t('liste.colonnes.litige')}</Tag>
            )}
            {r.estPrefinance && (
              <Tag color="gold" style={{ borderRadius: 18, fontSize: 11, marginInlineEnd: 0 }}>{t('liste.colonnes.prefinance')}</Tag>
            )}
          </div>
          <div style={{ minHeight: 22 }}>{renderRc(r)}</div>
        </div>
      ),
    },
    {
      title: t('liste.colonnes.date'),
      dataIndex: 'dateDeclaration',
      key: 'date',
      width: 150,
      format: 'date',
      onCell: (r) => r.estGroupeLiaison ? { colSpan: 0 } : {},
      render: (v, r) => (
        <div>
          <MetaLine strong>{formatDate(v)}</MetaLine>
          <MetaLine>{r.dateAccident ? t('liste.colonnes.accident', { date: formatDate(r.dateAccident) }) : null}</MetaLine>
        </div>
      ),
    },
    {
      title: '', key: 'actions', width: 92, fixed: 'right', exportable: false,
      onCell: (r) => r.estGroupeLiaison ? { colSpan: 0 } : {},
      render: (_, r) => (
        <Space size={2} onClick={(e) => e.stopPropagation()}>
          <Tooltip title={t('liste.actions.ouvrirDetail')}>
            <Button
              size="small"
              type="text"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/sinistres/${r.sinistreTrackingId}`)}
            >
              {t('liste.actions.details')}
            </Button>
          </Tooltip>
          <Dropdown
            trigger={['click']}
            menu={{
              items: [
                ...(r.statut === 'NOUVEAU' && r.typeSinistre !== 'SURVENU_ETRANGER' && can(HAB.SINISTRE_GARANTIE) ? [{
                  key: 'demander-garantie', icon: <CheckCircleOutlined />, label: t('liste.actions.demanderGarantie'), onClick: ({ domEvent }) => { domEvent.stopPropagation(); demanderConfirmationGarantie(r); },
                }] : []),
                { key: 'statut', icon: <SwapOutlined />, label: t('liste.actions.changerStatut'), onClick: ({ domEvent }) => { domEvent.stopPropagation(); setStatutModal({ open: true, sinistre: r }); } },
                ...(can(HAB.SINISTRE_ASSIGN) ? [{
                  key: 'affecter', icon: <UserSwitchOutlined />, label: t('liste.actions.affecterAgent'),
                  onClick: ({ domEvent }) => { domEvent.stopPropagation(); setAffectModal({ open: true, sinistre: r }); },
                }] : []),
                { type: 'divider' },
                { key: 'victimes', icon: <UserAddOutlined />, label: t('liste.actions.gererVictimes'), onClick: ({ domEvent }) => { domEvent.stopPropagation(); navigate(`/sinistres/${r.sinistreTrackingId}?tab=victimes`); } },
              ],
            }}
          >
            <Button
              size="small"
              icon={<MoreOutlined />}
              type="text"
              onClick={(e) => e.stopPropagation()}
            />
          </Dropdown>
        </Space>
      ),
    },
  ];

  /* Une sélection qu'on ne voit plus est une sélection qu'on ne contrôle plus :
     elle est vidée dès que le contenu de la table change (page, filtre, tri). */
  useEffect(() => { setSelection([]); }, [sinistres]);

  const dossiersSelectionnes = sinistres.filter(
    (r) => selection.includes(r.sinistreTrackingId)
  );

  const quitterSelection = () => { setModeSelection(false); setSelection([]); };

  const exportSubtitle = [
    filtreStatut && t('liste.export.statut', { statut: STATUT_MAP[filtreStatut]?.label || filtreStatut }),
    filtreLitige && t('liste.export.litige', { litige: filtreLitige }),
    filtreType && t('liste.export.type', { type: filtreType === 'SURVENU_TOGO' ? t('liste.filtres.etSurvenuTogo') : t('liste.filtres.teSurvenuEtranger') }),
    filtreAttente && (filtreAttente === 'OUI' ? t('liste.filtres.enAttenteTiers') : t('liste.filtres.sansAttenteExterieure')),
    criteres && t('liste.export.rechercheAvancee', { criteres: Object.entries(criteres)
      .filter(([, v]) => v).map(([k]) => (LIBELLE_CRITERE[k] ? t(LIBELLE_CRITERE[k]) : k)).join(', ') }),
    filtreOrigine && t('liste.export.origine', { origine: t(LIBELLE_ORIGINE[filtreOrigine]) }),
    dateRange?.[0] && dateRange?.[1] &&
    t('liste.export.declaration', { debut: dateRange[0].format('DD/MM/YYYY'), fin: dateRange[1].format('DD/MM/YYYY') }),
    search && t('liste.export.recherche', { search }),
  ].filter(Boolean).join(' · ') || t('liste.export.tousDossiers');

  return (
    <Layout title={t('liste.titre')}>
      {/* `total` vient de `pagination` (totalElements du backend) : c'est le nombre
          de dossiers repondant aux filtres, pas la seule page affichee. */}
      <PageHeader
        titre={t('liste.titre')}
        icone={<FolderOpenOutlined />}
        compte={total}
        unite={t('liste.unite')}
        actions={(
          <Space>
            {/* Passerelle vers la file d'attente mobile. N'apparait que s'il y a
                quelque chose a traiter : un bouton toujours visible vers une file
                vide devient un bouton qu'on cesse de regarder. Doublonne volontairement
                l'entree de menu - c'est ici que l'agent constate l'arrivee du flux. */}
            {nbMobilesAValider > 0 && can(HAB.MOBILE_DECLARATION_READ) && (
              <Button
                icon={<MobileOutlined />}
                style={{ borderRadius: 8, borderColor: '#db2777', color: '#db2777', fontWeight: 600 }}
                onClick={() => navigate('/mobile/declarations')}
              >
                {t('liste.mobilesAValider', { count: nbMobilesAValider })}
              </Button>
            )}
            {/* La route est gardee par la meme habilitation : le bouton ne doit pas
                proposer un ecran qui repondrait 403. */}
            {can(HAB.SINISTRE_CREATE) && (
              <Button
                type="primary" icon={<PlusOutlined />}
                style={{ background: GREEN, borderColor: GREEN, borderRadius: 8 }}
                onClick={() => navigate('/sinistres/nouveau')}
              >
                {t('liste.declarerSinistre')}
              </Button>
            )}
          </Space>
        )}
      />

      {/*  Indicateurs cles - vue d'ensemble stable, independante des filtres  */}
      <KpiCards items={kpiCles} columns={4} />

      {/*  Incoherence garanties confirmees / dossiers valides (Task 6) - n'apparait
          que si le backend detecte un ecart ; rien a afficher sinon.  */}
      {coherence && !coherence.coherent && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('liste.incoherence', { confirmees: coherence.nbGarantiesConfirmees, valides: coherence.nbValides, ecart: coherence.ecart })}
        />
      )}

      {/*  KPI par statut - tiroir dépliable, fermé par défaut : c'est à
          l'utilisateur de l'ouvrir quand il veut le détail par statut.  */}
      <Collapse
        bordered={false}
        expandIconPosition="end"
        className="cb-collapse"
        style={{
          marginBottom: 16,
          background: '#ffffff',
          border: '1px solid #e5e7eb',
          borderRadius: 8,
        }}
        items={[{
          key: 'kpi',
          label: (
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: TEXT }}>{t('liste.indicateurs.titre')}</div>
              <div style={{ fontSize: 12, color: MUTED }}>{t('liste.indicateurs.sousTitre')}</div>
            </div>
          ),
          extra: (
            <Tooltip title={t('liste.indicateurs.actualiser')}>
              <Button
                size="small"
                type="text"
                icon={<ReloadOutlined spin={kpiLoading} />}
                onClick={(e) => { e.stopPropagation(); loadKpiStats(); }}
              />
            </Tooltip>
          ),
          children: <KpiCards items={kpiItems} columns={4} style={{ marginBottom: 0 }} />,
        }]}
      />

      {/*  Vues et recherche - la seule barre visible par defaut.

           La recherche a ete SORTIE du tiroir : c'est le controle le plus utilise de
           l'ecran, le replier revenait a demander un clic avant chaque recherche.
           Les six filtres fins, eux, servent aux cas particuliers et restent replies. */}
      <div style={{
        display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center',
        marginBottom: 12,
      }}>
        <Search
          placeholder={t('liste.recherche.placeholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          loading={loading && !!debouncedSearch}
          allowClear
          style={{ width: 320 }}
        />
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {VUES.filter((v) => !v.propreAgent || moi?.trackingId).map((v) => {
            const actif = vueActive === v.cle;
            return (
              <Button
                key={v.cle}
                size="small"
                onClick={() => appliquerVue(v)}
                style={{
                  borderRadius: 20,
                  fontWeight: actif ? 700 : 500,
                  color: actif ? '#fff' : TEXT,
                  background: actif ? GREEN : '#fff',
                  borderColor: actif ? GREEN : '#e5e7eb',
                }}
              >
                {v.libelle}
              </Button>
            );
          })}
        </div>
        <Tooltip title={t('liste.actualiser')}>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => { loadSinistres(current, pageSize); loadKpiStats(); }}
            style={{ marginLeft: 'auto' }}
          />
        </Tooltip>
      </div>

      {/*  Filtres - tiroir dépliable, OUVERT par défaut (demande du 2026-09-15) : replié,
           il fallait un clic avant chaque filtrage.  */}
      <Collapse
        defaultActiveKey={['filtres']}
        bordered={false}
        expandIconPosition="end"
        className="cb-collapse"
        style={{
          marginBottom: 20,
          background: '#ffffff',
          border: '1px solid #e5e7eb',
          borderRadius: 8,
        }}
        items={[{
          key: 'filtres',
          label: (
            <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: TEXT }}>
                  {t('liste.filtres.titre')}
                  {activeFiltersCount > 0 && (
                    <Tag color="green" style={{ marginLeft: 8, borderRadius: 12 }}>
                      {t('liste.filtres.actifs', { count: activeFiltersCount })}
                    </Tag>
                  )}
                </div>
                <div style={{ fontSize: 12, color: MUTED }}>{t('liste.filtres.sousTitre')}</div>
              </div>
              {/* Réservé à ceux qui affectent : l'affectation en lot est la seule
                  action de masse, sans elle la sélection n'ouvre sur rien.

                  Sélection multiple : à gauche, contre le titre, et non dans le
                  groupe de droite. Elle change le COMPORTEMENT du tableau - un clic
                  sur une ligne coche au lieu d'ouvrir le dossier - alors que la
                  densité et « Réinitialiser » ne font que régler l'affichage des
                  filtres. Deux natures différentes n'ont pas à cohabiter au même
                  endroit.

                  `stopPropagation` : le libellé est la zone de clic qui déplie le
                  tiroir. Sans lui, actionner la bascule replierait les filtres au
                  passage. */}
              {can(HAB.SINISTRE_ASSIGN) && (
                <Space size={6} onClick={(e) => e.stopPropagation()}>
                  <Switch
                    size="small"
                    checked={modeSelection}
                    onChange={(v) => { setModeSelection(v); if (!v) setSelection([]); }}
                  />
                  <span style={{ fontSize: 12, color: MUTED }}>{t('liste.selection.multiple')}</span>
                </Space>
              )}
            </div>
          ),
          extra: (
            <Space wrap onClick={(e) => e.stopPropagation()}>
              {/* « Mes dossiers » est passe en vue nommee dans la barre du dessus. */}
              <Segmented
                size="small"
                value={density}
                onChange={setDensity}
                options={[
                  { label: t('liste.densite.confort'), value: 'comfortable' },
                  { label: t('liste.densite.compact'), value: 'compact' },
                ]}
              />
              <Button
                size="small"
                type={criteres ? 'primary' : 'default'}
                icon={<SearchOutlined />}
                onClick={() => setAvanceeOuverte(true)}
                style={criteres ? { background: GREEN, borderColor: GREEN } : undefined}
              >
                {t('liste.rechercheAvancee.bouton')}
              </Button>
              {activeFiltersCount > 0 && (
                <Button size="small" onClick={resetFilters}>
                  {t('liste.filtres.reinitialiser')}
                </Button>
              )}
            </Space>
          ),
          children: (
            <Row gutter={[12, 12]} align="middle">
              {/* La recherche vit desormais dans la barre visible au-dessus. */}
              <Col xs={24} sm={12} lg={4}>
                <Select
                  placeholder={t('liste.filtres.tousStatuts')}
                  style={{ width: '100%' }}
                  value={filtreStatut || undefined}
                  allowClear
                  onChange={(v) => { setFiltreStatut(v || ''); goFirstPage(); }}
                >
                  {STATUTS_SINISTRE.map(s => (
                    <Option key={s.value} value={s.value}>
                      <Tag color={s.color} style={{ borderRadius: 20 }}>{s.label}</Tag>
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} sm={12} lg={3}>
                <Select
                  placeholder={t('liste.filtres.attenteTiers')}
                  style={{ width: '100%' }}
                  value={filtreAttente || undefined}
                  allowClear
                  onChange={(v) => { setFiltreAttente(v || ''); goFirstPage(); }}
                >
                  <Option value="OUI">{t('liste.filtres.enAttenteTiers')}</Option>
                  <Option value="NON">{t('liste.filtres.sansAttenteExterieure')}</Option>
                </Select>
              </Col>
              <Col xs={24} sm={12} lg={3}>
                <Select
                  placeholder="ET / TE"
                  style={{ width: '100%' }}
                  value={filtreType || undefined}
                  allowClear
                  onChange={(v) => { setFiltreType(v || ''); goFirstPage(); }}
                >
                  <Option value="SURVENU_TOGO">{t('liste.filtres.etSurvenuTogo')}</Option>
                  <Option value="SURVENU_ETRANGER">{t('liste.filtres.teSurvenuEtranger')}</Option>
                </Select>
              </Col>
              <Col xs={24} sm={12} lg={3}>
                <Select
                  placeholder={t('liste.filtres.litige')}
                  style={{ width: '100%' }}
                  value={filtreLitige || undefined}
                  allowClear
                  onChange={(v) => { setFiltreLitige(v || ''); goFirstPage(); }}
                >
                  <Option value="EN_LITIGE">{t('liste.filtres.enLitigeTous')}</Option>
                  <Option value="CONTENTIEUX">{t('liste.filtres.contentieux')}</Option>
                  <Option value="ARBITRAGE">{t('liste.filtres.arbitrage')}</Option>
                  <Option value="HORS_LITIGE">{t('liste.filtres.horsLitige')}</Option>
                </Select>
              </Col>
              <Col xs={24} sm={12} lg={3}>
                {/* Origine du dossier : reprise historique, guichet, ou mobile - trois
                    ensembles disjoints, croisant `repriseHistorique` et `canal`. */}
                <Select
                  placeholder={t('liste.filtres.origineTous')}
                  style={{ width: '100%' }}
                  value={filtreOrigine || undefined}
                  allowClear
                  onChange={(v) => { setFiltreOrigine(v || ''); goFirstPage(); }}
                >
                  <Option value="REPRIS">{t('liste.filtres.repris')}</Option>
                  <Option value="GUICHET">{t('liste.filtres.saisisGuichet')}</Option>
                  <Option value="MOBILE">{t('liste.filtres.declaresMobile')}</Option>
                </Select>
              </Col>

              {/* « Retrouver facilement les traitements » d'un agent donné. */}
              {peutVoirLesAgents && (
                <Col xs={24} sm={12} lg={4}>
                  <Select
                    showSearch
                    filterOption={filtreSansAccents}
                    placeholder={t('liste.filtres.traitePar')}
                    style={{ width: '100%' }}
                    value={filtreRedacteur || undefined}
                    allowClear
                    optionFilterProp="label"
                    onChange={(v) => { setFiltreRedacteur(v || ''); goFirstPage(); }}
                    options={agents.map((a) => ({
                      value: a.utilisateurTrackingId,
                      // L'e-mail départage les homonymes lors de la recherche.
                      label: `${a.nomPrenom}${a.dossiersEnCours ? ` (${a.dossiersEnCours})` : ''} ${a.email || ''}`,
                    }))}
                  />
                </Col>
              )}

              <Col xs={24} lg={3}>
                <RangePicker
                  style={{ width: '100%' }}
                  format="DD/MM/YYYY"
                  placeholder={[t('liste.filtres.debut'), t('liste.filtres.fin')]}
                  value={dateRange}
                  onChange={(dates) => { setDateRange(dates); goFirstPage(); }}
                  allowClear
                />
              </Col>
            </Row>
          ),
        }]}
      />

      {/*  Table  */}
      <style>
        {`
          .cb-table .sinistre-row-even td { background: ${LIGHT}; }
          .cb-table .sinistre-row-odd td { background: #ffffff; }
          .cb-table .ant-table-tbody > tr:hover > td { background: #eef7f1 !important; }
          .cb-table .ant-table-thead > tr > th {
            background: #f3f4f6 !important;
            color: #374151;
            font-size: 12px;
            font-weight: 700;
          }
          .cb-collapse .ant-collapse-header { align-items: center !important; }
        `}
      </style>
      {modeSelection && (
        <div
          style={{
            display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
            marginBottom: 12, padding: '12px 16px', borderRadius: 10,
            background: selection.length ? '#ebf4ee' : '#fafafa',
            border: `1px solid ${selection.length ? '#c7e0cf' : '#e5e7eb'}`,
          }}
        >
          <span style={{ fontSize: 13, fontWeight: 600, color: selection.length ? '#134d2b' : MUTED }}>
            {selection.length
              ? t('liste.selection.nbSelectionnes', { count: selection.length })
              : t('liste.selection.cochez')}
          </span>

          {selection.length < sinistres.length && sinistres.length > 0 && (
            <Button
              size="small"
              onClick={() => setSelection(sinistres.map((r) => r.sinistreTrackingId))}
            >
              {t('liste.selection.toutSelectionner', { n: sinistres.length })}
            </Button>
          )}

          <div style={{ flex: 1 }} />

          <Button
            type="primary"
            size="small"
            icon={<UsergroupAddOutlined />}
            disabled={selection.length === 0}
            onClick={() => setAffectLotOpen(true)}
            style={selection.length ? { background: GREEN, borderColor: GREEN } : undefined}
          >
            {t('liste.selection.affecter')}
          </Button>
          <Button size="small" type="text" onClick={quitterSelection}>
            {t('liste.selection.quitter')}
          </Button>
        </div>
      )}

      <div className="cb-card" style={{ padding: 0 }}>
        <Table
          className="cb-table"
          dataSource={tableData}
          columns={columns}
          rowKey={(r) => r.estGroupeLiaison ? r.key : r.sinistreTrackingId}
          loading={loading}
          size={tableSize}
          scroll={{ x: 1380 }}
          rowClassName={(_, index) => index % 2 === 0 ? 'sinistre-row-even' : 'sinistre-row-odd'}
          rowSelection={modeSelection ? {
            selectedRowKeys: selection,
            onChange: setSelection,
            columnWidth: 48,
            getCheckboxProps: (r) => ({ disabled: r.estGroupeLiaison }),
          } : undefined}
          /* Deroule des dossiers lies : montre les FRERES du dossier, pas lui-meme.
             Les membres sont deja charges pour toute la page, ce rendu ne declenche
             donc aucun appel.
  
             Desactive en mode selection : la page y detourne deja le clic sur une
             ligne pour cocher au lieu d'ouvrir. Deux comportements de clic sur la
             meme ligne, c'est une confusion garantie. */
          expandable={modeSelection ? undefined : {
            rowExpandable: (r) => r.estGroupeLiaison,
            expandedRowRender: (r) => (
              <TableMembresLiaison
                membres={r.liaison.membres}
                onOuvrir={(id) => navigate(`/sinistres/${id}`)}
              />
            ),
          }}
          pagination={{
            current,
            pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: [25, 50, 100],
            showTotal: (totalLignes, range) => <span style={{ fontSize: 13, color: '#6b7280' }}>{t('liste.pagination.total', { debut: range[0], fin: range[1], total: totalLignes })}</span>,
          }}
          onChange={(next) => {
            setPagination(p => ({
              ...p,
              current: next.current || 1,
              pageSize: next.pageSize || p.pageSize,
            }));
          }}
          onRow={(r) => ({
            // En mode sélection, le clic coche la ligne au lieu d'ouvrir le dossier :
            // viser la seule case à cocher sur une ligne aussi haute est pénible.
            onClick: () => {
              if (r.estGroupeLiaison) return; // laisser antd gérer l'expand
              if (!modeSelection) { navigate(`/sinistres/${r.sinistreTrackingId}`); return; }
              const id = r.sinistreTrackingId;
              setSelection((prev) => prev.includes(id)
                ? prev.filter((k) => k !== id)
                : [...prev, id]);
            },
            style: { cursor: 'pointer' },
          })}
        />
      </div>

      <TableExportButton
        floating
        data={sinistres}
        columns={columns}
        filename="sinistres"
        title={t('liste.export.titre')}
        subtitle={t('liste.export.sousTitre', { filtres: exportSubtitle })}
        count={sinistres.length}
      />

      <AffecterEnMasseModal
        open={affectLotOpen}
        dossiers={dossiersSelectionnes}
        onClose={() => setAffectLotOpen(false)}
        onAffecte={() => {
          quitterSelection();
          loadSinistres(current, pageSize);
        }}
      />

      <ChangerStatutModal
        open={statutModal.open}
        sinistre={statutModal.sinistre}
        onClose={() => setStatutModal({ open: false, sinistre: null })}
        onChanged={() => { loadSinistres(current, pageSize); loadKpiStats(); }}
      />

      <RechercheAvanceeModal
        open={avanceeOuverte}
        valeurs={criteres}
        onClose={() => setAvanceeOuverte(false)}
        onChercher={(v) => {
          setCriteres(v);
          setAvanceeOuverte(false);
          goFirstPage();
        }}
        onEffacer={() => { setCriteres(null); setAvanceeOuverte(false); goFirstPage(); }}
      />

      {/* Affectation directe depuis la liste : le destinataire est notifié (cloche in-app). */}
      <AffecterDossierModal
        open={affectModal.open}
        sinistre={affectModal.sinistre || {}}
        onClose={() => setAffectModal({ open: false, sinistre: null })}
        onAffecte={() => { loadSinistres(current, pageSize); }}
      />
    </Layout>
  );
}

/**
 * Saisie des criteres de recherche avancee.
 *
 * Un formulaire et pas une barre de plus dans l'en-tete : six champs poses en
 * permanence au-dessus de la liste la repousseraient sous la ligne de flottaison
 * pour un usage occasionnel.
 */
function RechercheAvanceeModal({ open, valeurs, onClose, onChercher, onEffacer }) {
  const { t } = useTranslation('sinistres');
  const [form] = Form.useForm();

  useEffect(() => {
    if (open) form.setFieldsValue(valeurs || {});
  }, [open, valeurs, form]);

  const soumettre = (v) => {
    const retenus = Object.fromEntries(
      Object.entries(v).filter(([, valeur]) => valeur && String(valeur).trim()),
    );
    // Aucun critere : on revient a la liste ordinaire plutot que de lancer une
    // requete qui balaierait la table jointe aux victimes.
    if (Object.keys(retenus).length === 0) { onEffacer(); return; }
    onChercher(retenus);
  };

  return (
    <CBModal
      open={open}
      onCancel={onClose}
      title={t('liste.rechercheAvancee.titre')}
      footer={null}
      width={620}
      destroyOnClose
    >
      <p style={{ fontSize: 12.5, color: MUTED, marginTop: 0 }}>
        <Trans t={t} i18nKey="liste.rechercheAvancee.intro" components={{ strong: <strong /> }} />
      </p>
      <Form form={form} layout="vertical" onFinish={soumettre}>
        <Row gutter={16}>
          {CRITERES_AVANCES.map((c) => (
            <Col xs={24} sm={12} key={c.cle}>
              <Form.Item name={c.cle} label={t(c.libelle)} extra={c.aide && t(c.aide)}>
                <Input placeholder={c.exemple} allowClear />
              </Form.Item>
            </Col>
          ))}
        </Row>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, borderTop: '1px solid #e5e7eb', paddingTop: 16 }}>
          <Button onClick={() => { form.resetFields(); onEffacer(); }}>
            {t('liste.rechercheAvancee.effacer')}
          </Button>
          <Space>
            <Button onClick={onClose}>{t('liste.rechercheAvancee.annuler')}</Button>
            <Button type="primary" htmlType="submit" style={{ background: GREEN, borderColor: GREEN }}>
              {t('liste.rechercheAvancee.rechercher')}
            </Button>
          </Space>
        </div>
      </Form>
    </CBModal>
  );
}
